import type { FlightState } from "@b2bportal/core-flights";
import { TripCategory } from "@b2bportal/core-types";
import dayjs from "dayjs";
import {
  getIsFtc,
  getItineraryDepDate,
  getItineraryDest,
  getItineraryOrigin,
  getItineraryRetDate,
  getItineraryTripType,
} from "./policySelectors";

export const getSearchStore = (state: FlightState) =>
  state.flights.flightSearch;
export const getExchangeSearchStore = (state: FlightState) =>
  state.flights.flightExchange.search;

export const getAirportsChanged = (state: FlightState) => {
  const newDest = getNewDestination(state);
  const newOrigin = getOrigin(state);
  const originalDest = getItineraryDest(state);
  const originalOrigin = getItineraryOrigin(state);

  const destCode = newDest?.id?.code?.code;
  const originCode = newOrigin?.id?.code?.code;
  const destChanged =
    destCode != null && destCode !== originalDest?.terminalCode;
  const originChanged =
    originCode != null && originCode !== originalOrigin?.terminalCode;

  return !!(destChanged || originChanged);
};

export const getDatesChanged = (state: FlightState) => {
  const originalRetDate = getItineraryRetDate(state);
  const sameDepDate = getIsSameDepDate(state);
  const sameRetDate = getIsSameRetDate(state);

  if (originalRetDate != null) {
    return !sameDepDate || !sameRetDate;
  }

  return !sameDepDate;
};

// export const getAwaitingRefetch = (state: FlightState) =>
//   getSearchStore(state).awaitingRefetch;

// export const getCalendarA11yEnabled = (state: FlightState) =>
//   getSearchStore(state).calendarA11yEnabled;

// export const getCalendarVisited = (state: FlightState) =>
//   getSearchStore(state).calendarVisited;

// export const getCurrency = (state: FlightState) =>
//   getSearchStore(state).currency;

export const getNewDepartureDate = (state: FlightState) =>
  getSearchStore(state).departureDate;

// export const getDepartureDateBuckets = (state: FlightState) =>
//   getSearchStore(state).departureDateBuckets;

// export const getDepartureDateBucketsLoading = (state: FlightState) =>
//   getSearchStore(state).departureDateBucketsLoading;

// export const getDepartureMonths = (state: FlightState) =>
//   getSearchStore(state).departureMonths;

export const getNewDestination = (state: FlightState) =>
  getSearchStore(state).destination;

// export const getDestinationCategories = (state: FlightState) =>
//   getSearchStore(state).destinationCategories;

// export const getDestinationCategoriesLoading = (state: FlightState) =>
//   getSearchStore(state).destinationCategoriesLoading;

// export const getDestinationType = (state: FlightState) =>
//   getSearchStore(state).destinationType;

// export const getIsMissingDate = (state: FlightState) =>
//   getSearchStore(state).missingDate;

export const getExchangeForType = (state: FlightState) => {
  const { exchangeFor, outboundSelection, returnSelection } =
    getExchangeSearchStore(state);

  return {
    exchangeFor,
    outboundSelection,
    returnSelection,
  };
};

export const getIsSameDepDate = (state: FlightState) => {
  const newDepDate = getNewDepartureDate(state);
  const originalDepDate = getItineraryDepDate(state);

  return dayjs(newDepDate).isSame(originalDepDate, "day");
};

export const getIsSameDest = (state: FlightState) => {
  const newDest = getNewDestination(state);
  const originalDest = getItineraryDest(state);

  return newDest?.id?.code?.code === originalDest?.locationCode;
};

export const getIsSameOrigin = (state: FlightState) => {
  const origin = getOrigin(state);
  const originalOrigin = getItineraryOrigin(state);

  return origin?.id?.code?.code === originalOrigin?.locationCode;
};

export const getIsSameRetDate = (state: FlightState) => {
  const newReturnDate = getReturnDate(state);
  const originalRetDate = getItineraryRetDate(state);

  if (originalRetDate != null) {
    return dayjs(newReturnDate).isSame(originalRetDate, "day");
  }

  return false;
};

// export const getMobileSearchProgress = (state: FlightState) =>
//   getSearchStore(state).mobileSearchStep;

export const getNumTravelers = (state: FlightState) => {
  const { adultsCount, childrenCount, infantsInSeatCount, infantsOnLapCount } =
    getSearchStore(state).passengerCounts;

  return adultsCount + childrenCount + infantsInSeatCount + infantsOnLapCount;
};

// export const getOpenDatesModal = (state: FlightState) =>
//   getSearchStore(state).openDatesModal;

export const getOrigin = (state: FlightState) => getSearchStore(state).origin;

// export const getOriginCategories = (state: FlightState) =>
//   getSearchStore(state).originCategories;

// export const getOriginCategoriesLoading = (state: FlightState) =>
//   getSearchStore(state).originCategoriesLoading;

// export const getOriginType = (state: FlightState) =>
//   getSearchStore(state).originType;

// export const getPreferences = (state: FlightState) =>
//   getSearchStore(state).preferences;

// export const getRenderSearchInput = (state: FlightState) =>
//   getSearchStore(state).renderSearchInput;

export const getOutboundChanged = (state: FlightState) => {
  const sameDepDate = getIsSameDepDate(state);
  const sameDest = getIsSameDest(state);
  const sameOrigin = getIsSameOrigin(state);

  return !(sameOrigin && sameDest && sameDepDate);
};

export const getReturnChanged = (state: FlightState) => {
  const sameDest = getIsSameDest(state);
  const sameOrigin = getIsSameOrigin(state);
  const sameRetDate = getIsSameRetDate(state);

  return !(sameOrigin && sameDest && sameRetDate);
};

export const getReturnDate = (state: FlightState) =>
  getSearchStore(state).returnDate;

export const getShowSlicePicker = (state: FlightState) => {
  const isFtc = getIsFtc(state);
  const itineraryTripType = getItineraryTripType(state);
  const outboundChanged = getOutboundChanged(state);
  const returnChanged = getReturnChanged(state);
  const isRoundTrip = itineraryTripType === TripCategory.ROUND_TRIP;

  if (!isFtc && isRoundTrip) {
    return !outboundChanged || !returnChanged;
  }

  return false;
};

export const getStopsOption = (state: FlightState) =>
  getSearchStore(state).stopsOption;

export const getTripCategory = (state: FlightState) =>
  getSearchStore(state).tripCategory;

export const getOutboundSelection = (state: FlightState) =>
  getExchangeSearchStore(state).outboundSelection;

export const getReturnSelection = (state: FlightState) =>
  getExchangeSearchStore(state).returnSelection;
